import { useSearchSuggestionsQuery } from "@faire/deprecated-retailer-visitor-shared/features/SearchSuggestions-v2/DataFetching/useSearchSuggestionsQuery";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useSettingEnableNLSSearch } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableNLSSearch";
import { useShouldShowLoggedOutTopHeaderSearchSuggestionWithSetting } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingLoggedOutTopHeaderSearchSuggestion";
import { setIntervalIfPossible } from "@faire/next-utils/isomorphic/setIntervalIfPossible";
import { match as matchBrandRoute } from "@faire/web-api--source/routes/www/brand/brandToken/match";
import { useStrictLocalization } from "@faire/web/common/localization";
import shuffle from "lodash/shuffle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useLoggedOutBrandTopSearchPlaceholder } from "./useLoggedOutBrandTopSearchPlaceholder";
import {
  useGetCategoryIdentifier,
  useLoggedOutCategoryTopSearchPlaceholder,
} from "./useLoggedOutCategoryTopSearchPlaceholder";
import { useLoggedOutSearchTopSearchPlaceholder } from "./useLoggedOutSearchTopSearchPlaceholder";

const CYCLING_SPEED = 5000;
const EMPTY_ARRAY: string[] = [];

export const useSearchPlaceholderWithLoggedOutSearchSuggestionXP = (
  query: string
) => {
  const { placeholder, startPlaceholderCycling, stopPlaceholderCycling } =
    useSearchPlaceholder();

  const pathname = useLocation().pathname;

  const shouldShowLoggedOutTopHeaderSearchSuggestionWithSetting =
    useShouldShowLoggedOutTopHeaderSearchSuggestionWithSetting();

  const categoryPathIdentifier = useGetCategoryIdentifier();

  const brandToken = matchBrandRoute(pathname)?.params.brandToken;

  //Get placeholder for different pages, will only enable fetching if the setting is enabled
  const categoryPlaceholder = useLoggedOutCategoryTopSearchPlaceholder(
    shouldShowLoggedOutTopHeaderSearchSuggestionWithSetting
      ? categoryPathIdentifier
      : undefined
  );

  const brandPlaceholder = useLoggedOutBrandTopSearchPlaceholder(
    shouldShowLoggedOutTopHeaderSearchSuggestionWithSetting
      ? brandToken
      : undefined
  );

  const searchAndDiscoverPlaceholder = useLoggedOutSearchTopSearchPlaceholder(
    shouldShowLoggedOutTopHeaderSearchSuggestionWithSetting ? query : undefined
  );

  const getPlaceholder = () => {
    if (shouldShowLoggedOutTopHeaderSearchSuggestionWithSetting) {
      if (categoryPathIdentifier) {
        return categoryPlaceholder;
      } else if (brandToken) {
        return brandPlaceholder;
      } else {
        return searchAndDiscoverPlaceholder;
      }
    } else {
      return placeholder;
    }
  };

  return {
    placeholder: getPlaceholder(),
    startPlaceholderCycling:
      shouldShowLoggedOutTopHeaderSearchSuggestionWithSetting
        ? () => {}
        : startPlaceholderCycling,
    stopPlaceholderCycling:
      shouldShowLoggedOutTopHeaderSearchSuggestionWithSetting
        ? () => {}
        : stopPlaceholderCycling,
  };
};

export const useSearchPlaceholder = () => {
  const [placeholderSuggestion, setPlaceholderSuggestion] = useState<
    string | undefined
  >(undefined);
  const { strictLocalize } = useStrictLocalization();
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const { data } = useSearchSuggestionsQuery("", {
    enabled: isLoggedInRetailer,
  });

  const shouldShowNLSSearchPlaceholder = useSettingEnableNLSSearch();

  const apparelSearchPlaceholders = useMemo(() => {
    if (!isLoggedInRetailer || shouldShowNLSSearchPlaceholder) {
      return EMPTY_ARRAY;
    }
    return shuffle(data?.search_recommendations).map(({ name }) =>
      strictLocalize(
        {
          id: "Q3+gog",
          defaultMessage: "Search for “{searchProductName}”",
          description: {
            text: "Suggested search term for the search bar",
          },
        },
        {
          searchProductName: name ?? "",
        }
      )
    );
  }, [data?.search_recommendations, isLoggedInRetailer, strictLocalize]);

  const cycleInterval = useRef<number | undefined>(undefined);
  const currentIndex = useRef(0);
  const emptySuggestions = apparelSearchPlaceholders.length === 0;
  const placeholder = useMemo(() => {
    if (placeholderSuggestion) {
      return placeholderSuggestion;
    }
    const loggedInPlaceholderDefault = strictLocalize({
      id: "z7d8Af",
      defaultMessage: "Search products or brands",
      description: {
        text: "Text for search box.",
      },
    });
    const loggedInPlaceholderWithNLSSearch = strictLocalize({
      id: "/Z0Ooa",
      defaultMessage: "Search or use a phrase",
      description: {
        text: "Text for search box.",
      },
    });
    const loggedInPlaceholder = shouldShowNLSSearchPlaceholder
      ? loggedInPlaceholderWithNLSSearch
      : loggedInPlaceholderDefault;

    const loggedOutPlaceholder = strictLocalize({
      id: "KChW/3",
      defaultMessage: "Search wholesale products or brands",
      description: {
        text: "Text for search box.",
      },
    });

    return isLoggedInRetailer ? loggedInPlaceholder : loggedOutPlaceholder;
  }, [placeholderSuggestion, strictLocalize, isLoggedInRetailer]);

  const updatePlaceholderSuggestion = useCallback(() => {
    if (emptySuggestions) {
      return;
    }
    setPlaceholderSuggestion(apparelSearchPlaceholders[currentIndex.current]);
    currentIndex.current =
      (currentIndex.current + 1) % apparelSearchPlaceholders.length;
  }, [apparelSearchPlaceholders, emptySuggestions]);

  const startPlaceholderCycling = useCallback(() => {
    if (cycleInterval.current) {
      return;
    }

    updatePlaceholderSuggestion();
    cycleInterval.current = setIntervalIfPossible(
      updatePlaceholderSuggestion,
      CYCLING_SPEED
    );
  }, [updatePlaceholderSuggestion]);

  const stopPlaceholderCycling = useCallback(() => {
    if (cycleInterval.current) {
      clearInterval(cycleInterval.current);
      setPlaceholderSuggestion(undefined);
      cycleInterval.current = undefined;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearInterval(cycleInterval.current);
    };
  }, []);

  return {
    placeholder,
    startPlaceholderCycling,
    stopPlaceholderCycling,
  };
};
